import React, { useState, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  InputNumber,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Spin,
  message,
  Layout,
  Tag,
  theme,
  Statistic,
  FloatButton,
} from "antd";
import {
  RocketOutlined,
  GlobalOutlined,
  BookOutlined,
  UserOutlined,
  BankOutlined,
  DollarCircleOutlined,
  CheckCircleOutlined,
  HighlightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

const { Title, Text, Paragraph } = Typography;
const { Header, Content, Footer } = Layout;
const { Option } = Select;
const { TextArea } = Input;

// --- Interfaces ---
interface StudyAbroadFormValues {
  fullName: string;
  currentEducation: string;
  gpa: number;
  ielts: number;
  targetCountry: string;
  major: string;
  budget: string;
  specificQuestions: string;
}

const StudyAbroadConsultant: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<string>("");
  const [form] = Form.useForm();

  // Ref to auto-scroll to results
  const resultRef = useRef<HTMLDivElement>(null);

  // Access Ant Design theme tokens for inline styling consistency
  const { token } = theme.useToken();

  // --- Handle Submission ---
  const onFinish = async (values: StudyAbroadFormValues) => {
    setLoading(true);
    setResult("");

    // Scroll slightly down to show loading state
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    const prompt = `
      Đóng vai một chuyên gia tư vấn giáo dục quốc tế cao cấp.
      Hãy phân tích hồ sơ dưới đây và đưa ra lộ trình chi tiết.

      THÔNG TIN HỒ SƠ:
      - Họ tên: ${values.fullName}
      - Trình độ: ${values.currentEducation}
      - GPA: ${values.gpa}/10
      - IELTS/TOEFL: ${values.ielts}
      - Nước đến: ${values.targetCountry}
      - Ngành: ${values.major}
      - Ngân sách: ${values.budget}
      - Note: ${values.specificQuestions || "Không có"}

      YÊU CẦU ĐẦU RA (Markdown):
      1. **Lời chào & Nhận định tổng quan:** Đánh giá tiềm năng hồ sơ ngắn gọn, tích cực.
      2. **Phân tích SWOT:** Điểm mạnh/Yếu.
      3. **Chiến lược chọn trường (Top 3):** Gợi ý cụ thể kèm lý do.
      4. **Bức tranh tài chính:** Học phí & sinh hoạt phí dự kiến.
      5. **Săn học bổng:** Chiến thuật tối ưu chi phí.
      6. **Lộ trình hành động (Timeline):** Các bước từ giờ đến lúc bay.

      QUY TẮC ĐỊNH DẠNG (BẮT BUỘC):
      - TUYỆT ĐỐI KHÔNG thêm phần Chữ ký (Ví dụ: "Thân mến", "Ký tên...", "Chuyên gia tư vấn").
      - KHÔNG viết đoạn kết luận, lời khuyên chung hoặc lời chúc ở cuối cùng (Hệ thống sẽ tự động thêm phần này).
      - Dừng lại ngay sau khi kết thúc mục 6.
      
      Văn phong: Chuyên nghiệp, khách quan nhưng thân thiện.
    `;

    try {
      const response = await axios.post(
        "https://groqprompt.netlify.app/api/ai",
        {
          prompt: prompt,
        },
      );

      if (response.data && response.data.result) {
        setResult(response.data.result);
        message.success({
          content: "Đã hoàn tất phân tích hồ sơ!",
          icon: <CheckCircleOutlined style={{ color: token.colorSuccess }} />,
        });
      } else {
        message.warning("Hệ thống đang bận, vui lòng thử lại.");
      }
    } catch (error) {
      console.error("Error:", error);
      message.error("Kết nối gián đoạn, vui lòng kiểm tra mạng.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: token.colorBgLayout }}>
      {/* --- HEADER --- */}
      <Header
        className="glass-header"
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "0 24px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            maxWidth: 800,
          }}
        >
          <GlobalOutlined
            style={{
              fontSize: "28px",
              color: token.colorPrimary,
              marginRight: "12px",
            }}
          />
          <div>
            <Title
              level={4}
              style={{ margin: 0, color: token.colorPrimary, lineHeight: 1 }}
            >
              Tư vấn du học AI
            </Title>
          </div>
        </div>
      </Header>

      <Content style={{ padding: "0 24px 40px" }}>
        {/* --- MAIN CONTAINER (Centered Single Column) --- */}
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          {/* --- HERO SECTION --- */}
          <div style={{ textAlign: "center", padding: "50px 0 40px" }}>
            <Title
              level={2}
              className="gradient-text"
              style={{ marginBottom: 12, fontWeight: "bold" }}
            >
              Khám Phá Hành Trình Du Học Của Bạn
            </Title>
            <Paragraph
              type="secondary"
              style={{ fontSize: 16, maxWidth: 600, margin: "0 auto 30px" }}
            >
              Nhập thông tin hồ sơ bên dưới để AI phân tích tiềm năng, gợi ý
              trường và xây dựng lộ trình săn học bổng tối ưu.
            </Paragraph>

            {/* Moved Statistics to Hero Section */}
            <Row justify="center" gutter={[48, 16]}>
              <Col>
                <Statistic
                  title="Dữ liệu trường"
                  value="5000+"
                  valueStyle={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: token.colorPrimary,
                  }}
                />
              </Col>
              <Col>
                <Statistic
                  title="Cơ hội học bổng"
                  value="10K+"
                  valueStyle={{
                    fontSize: 20,
                    fontWeight: "bold",
                    color: token.colorPrimary,
                  }}
                />
              </Col>
            </Row>
          </div>

          {/* --- INPUT FORM CARD --- */}
          <Card
            bordered={false}
            style={{
              borderRadius: token.borderRadiusLG,
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
              overflow: "hidden",
              marginBottom: 30,
            }}
            title={
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <UserOutlined style={{ color: token.colorPrimary }} /> Thiết Lập
                Hồ Sơ
              </span>
            }
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{
                targetCountry: "Mỹ",
                currentEducation: "Đã tốt nghiệp",
                budget: "25,000 - 40,000 USD",
              }}
            >
              <div
                style={{
                  background: "#f9f9f9",
                  padding: 24,
                  borderRadius: 8,
                  marginBottom: 24,
                }}
              >
                <Form.Item
                  name="fullName"
                  label="Họ và Tên học viên"
                  rules={[{ required: true, message: "Vui lòng nhập tên" }]}
                  style={{ marginBottom: 16 }}
                >
                  <Input
                    placeholder="Ví dụ: Nguyễn Minh Nhật"
                    prefix={<UserOutlined className="site-form-item-icon" />}
                  />
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="currentEducation"
                      label="Trình độ hiện tại"
                    >
                      <Select>
                        <Option value="Lớp 9">Lớp 9</Option>
                        <Option value="Lớp 10">Lớp 10</Option>
                        <Option value="Lớp 11">Lớp 11</Option>
                        <Option value="Lớp 12">Lớp 12 (THPT)</Option>
                        <Option value="Sinh viên">Sinh viên ĐH</Option>
                        <Option value="Đã tốt nghiệp">Đã đi làm</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="targetCountry" label="Điểm đến mơ ước">
                      <Select
                        showSearch
                        placeholder="Chọn quốc gia"
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {/* Châu Mỹ & Úc */}
                        <Option value="Mỹ">🇺🇸 Mỹ (USA)</Option>
                        <Option value="Canada">🇨🇦 Canada</Option>
                        <Option value="Úc">🇦🇺 Úc (Australia)</Option>
                        <Option value="New Zealand">🇳🇿 New Zealand</Option>

                        {/* Châu Âu */}
                        <Option value="Anh">🇬🇧 Anh Quốc (UK)</Option>
                        <Option value="Đức">🇩🇪 Đức (Germany)</Option>
                        <Option value="Pháp">🇫🇷 Pháp (France)</Option>
                        <Option value="Hà Lan">🇳🇱 Hà Lan (Netherlands)</Option>
                        <Option value="Phần Lan">🇫🇮 Phần Lan (Finland)</Option>
                        <Option value="Thụy Sĩ">
                          🇨🇭 Thụy Sĩ (Switzerland)
                        </Option>
                        <Option value="Tây Ban Nha">
                          🇪🇸 Tây Ban Nha (Spain)
                        </Option>

                        {/* Châu Á */}
                        <Option value="Singapore">🇸🇬 Singapore</Option>
                        <Option value="Nhật Bản">🇯🇵 Nhật Bản (Japan)</Option>
                        <Option value="Hàn Quốc">🇰🇷 Hàn Quốc (Korea)</Option>
                        <Option value="Trung Quốc">
                          🇨🇳 Trung Quốc (China)
                        </Option>
                        <Option value="Đài Loan">🇹🇼 Đài Loan (Taiwan)</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div style={{ marginBottom: 24 }}>
                <Text strong style={{ display: "block", marginBottom: 12 }}>
                  Năng lực học thuật & Ngành nghề
                </Text>
                <Row gutter={24}>
                  <Col xs={12} sm={6}>
                    <Form.Item
                      name="gpa"
                      label="GPA (Thang 10)"
                      rules={[{ required: true, message: "Nhập GPA" }]}
                    >
                      <InputNumber
                        min={0}
                        max={10}
                        step={0.1}
                        style={{ width: "100%" }}
                        prefix={<HighlightOutlined />}
                        placeholder="8.0"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item
                      name="ielts"
                      label="IELTS"
                      rules={[{ required: true, message: "Nhập điểm" }]}
                    >
                      <InputNumber
                        min={0}
                        max={9.0}
                        step={0.5}
                        style={{ width: "100%" }}
                        prefix={<GlobalOutlined />}
                        placeholder="6.5"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="major"
                      label="Ngành học mong muốn"
                      rules={[
                        { required: true, message: "Bạn muốn học ngành gì?" },
                      ]}
                    >
                      <Input
                        prefix={<BookOutlined />}
                        placeholder="Công nghệ thông tin..."
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <Form.Item
                name="budget"
                label="Khả năng tài chính (Mỗi năm)"
                rules={[{ required: true }]}
              >
                <Select suffixIcon={<DollarCircleOutlined />}>
                  <Option value="Dưới 15,000 USD">
                    {"<"} 15,000 USD (Tiết kiệm)
                  </Option>
                  <Option value="15,000 - 25,000 USD">
                    15k - 25k USD (Trung bình)
                  </Option>
                  <Option value="25,000 - 40,000 USD">
                    25k - 40k USD (Khá)
                  </Option>
                  <Option value="Trên 40,000 USD">
                    {">"} 40,000 USD (Thoải mái)
                  </Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="specificQuestions"
                label="Ghi chú / Thắc mắc thêm"
              >
                <TextArea
                  rows={3}
                  placeholder="VD: Em muốn tìm trường ở bang California, có người thân..."
                  style={{ resize: "none" }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0, marginTop: 30 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<RocketOutlined />}
                  loading={loading}
                  block
                  size="large"
                  shape="round"
                  style={{
                    height: 54,
                    fontSize: 18,
                    background:
                      "linear-gradient(90deg, #003eb3 0%, #096dd9 100%)",
                    border: "none",
                    boxShadow: "0 4px 14px 0 rgba(0, 62, 179, 0.39)",
                  }}
                >
                  {loading
                    ? "AI Đang Phân Tích Dữ Liệu..."
                    : "Phân Tích Hồ Sơ Ngay"}
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* --- RESULT SECTION (CONDITIONALLY RENDERED) --- */}
          <div ref={resultRef}>
            {(loading || result) && (
              <Card
                bordered={false}
                style={{
                  minHeight: "400px",
                  borderRadius: token.borderRadiusLG,
                  boxShadow: "0 10px 30px -10px rgba(0,0,0,0.08)",
                  animation: "fadeIn 0.5s ease-in-out",
                }}
              >
                {loading && (
                  <div style={{ textAlign: "center", padding: "80px 0" }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 24, color: token.colorPrimary }}>
                      <Text strong style={{ fontSize: 18 }}>
                        Đang xây dựng lộ trình...
                      </Text>
                      <br />
                      <Text type="secondary">
                        Đang tra cứu học bổng & xếp hạng trường phù hợp với bạn
                      </Text>
                    </div>
                  </div>
                )}

                {result && !loading && (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 24,
                        borderBottom: "1px solid #eee",
                        paddingBottom: 15,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            background: "#fff7e6",
                            padding: 8,
                            borderRadius: "50%",
                          }}
                        >
                          <BankOutlined
                            style={{ fontSize: 24, color: token.colorWarning }}
                          />
                        </div>

                        <Title level={3} style={{ margin: 0 }}>
                          Kết Quả Tư Vấn
                        </Title>
                      </div>
                      <Tag
                        color="blue"
                        style={{ fontSize: 14, padding: "4px 10px" }}
                      >
                        AI Powered
                      </Tag>
                    </div>

                    <div className="markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {result}
                      </ReactMarkdown>
                    </div>

                    <Divider style={{ margin: "40px 0" }} />

                    <div
                      style={{
                        background: "#f6ffed",
                        border: "1px solid #b7eb8f",
                        padding: 24,
                        borderRadius: 8,
                        textAlign: "center",
                      }}
                    >
                      <Text strong type="success" style={{ fontSize: 16 }}>
                        <CheckCircleOutlined /> Lời khuyên tiếp theo:
                      </Text>
                      <Paragraph style={{ marginTop: 8 }}>
                        Đây là lộ trình tham khảo dựa trên dữ liệu bạn cung cấp.
                        Hãy lưu lại và liên hệ chuyên viên để được hỗ trợ làm hồ
                        sơ thực tế.
                      </Paragraph>
                      <Button
                        type="primary"
                        style={{
                          background: token.colorSuccess,
                          borderColor: token.colorSuccess,
                        }}
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        Chỉnh sửa hồ sơ
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: "center",
          background: "transparent",
          color: "#888",
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          © {new Date().getFullYear()} AI Global Education. Designed for Future
          Leaders.
        </Text>
      </Footer>
      <FloatButton.BackTop
        visibilityHeight={400}
        type="primary"
        shape="circle"
        duration={600}
      />
    </Layout>
  );
};

export default StudyAbroadConsultant;
